import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { AuthLayout } from 'src/layouts/auth/AuthLayout';
import { SetupLayout } from 'src/layouts/procurer/SetupLayout';
import { SupplierLayouts, PublicContractsLayout } from 'src/layouts';
import { ProtectedLayout } from 'src/layouts/shared/ProtectedLayout';

import { SplashScreen } from 'src/components/LoadingScreen';

const ContractStartPage = lazy(() => import('src/pages/procurer/contract/Start'));
const GeographyPage = lazy(() => import('src/pages/procurer/preferences/Geography'));
const ThemesPage = lazy(() => import('src/pages/procurer/preferences/Themes'));
const ImpactPage = lazy(() => import('src/pages/procurer/preferences/Impact'));
const QualitativePage = lazy(() => import('src/pages/procurer/preferences/Qualitative'));
const ScoringPage = lazy(() => import('src/pages/procurer/preferences/Scoring'));
const FinalizingPage = lazy(() => import('src/pages/procurer/preferences/Finalizing'));
const CommitmentsPage = lazy(() => import('src/pages/procurer/contract/Commitments'));
const PreviousDeliveryPage = lazy(() => import('src/pages/procurer/contract/PreviousDelivery'));
const RequirementsPage = lazy(() => import('src/pages/procurer/contract/Requirements'));
const BackfillReportPage = lazy(() => import('src/pages/supplier/SubmitReport'));
const SuccessPage = lazy(() => import('src/pages/procurer/preferences/Success'));
const ContractSuccessPage = lazy(() => import('src/pages/procurer/contract/Success'));
const SingleApplicationPage = lazy(
  () => import('src/pages/procurer/applications/SingleApplication')
);
const ReviewReportPage = lazy(() => import('src/pages/procurer/ReviewReport'));

export const procurerRoutes: RouteObject[] = [
  {
    path: 'procurer',
    element: (
      <Suspense fallback={<SplashScreen />}>
        <ProtectedLayout allowedRoles={['PROCURER', 'ADMIN']} />
      </Suspense>
    ),
    children: [
      {
        path: 'preferences',
        element: (
          <SetupLayout mode="preferences">
            <Outlet />
          </SetupLayout>
        ),
        children: [
          {
            path: 'geography',
            element: <GeographyPage />,
          },
          {
            path: 'themes',
            element: <ThemesPage />,
          },
          {
            path: 'impact',
            element: <ImpactPage />,
          },
          {
            path: 'scoring',
            element: <ScoringPage />,
          },
          {
            path: 'qualitative',
            element: <QualitativePage />,
          },
          {
            path: 'finalizing',
            element: <FinalizingPage />,
          },
        ],
      },
      {
        path: 'contract',
        element: (
          <SetupLayout mode="contract">
            <Outlet />
          </SetupLayout>
        ),
        children: [
          {
            path: 'start',
            element: <ContractStartPage />,
          },
          {
            path: ':contractId',
            children: [
              {
                path: 'start',
                element: <ContractStartPage />,
              },
              {
                path: 'details',
                element: <ContractStartPage />,
              },
              {
                path: 'geography',
                element: <GeographyPage />,
              },
              {
                path: 'themes',
                element: <ThemesPage />,
              },
              {
                path: 'impact',
                element: <ImpactPage />,
              },
              {
                path: 'scoring',
                element: <ScoringPage />,
              },
              {
                path: 'qualitative',
                element: <QualitativePage />,
              },
              {
                path: 'finalizing',
                element: <FinalizingPage />,
              },
              {
                path: 'commitments',
                element: <CommitmentsPage />,
              },
              {
                path: 'previous-delivery',
                element: <PreviousDeliveryPage />,
              },
              {
                path: 'requirements',
                element: <RequirementsPage />,
              },
            ],
          },
        ],
      },
      {
        path: 'contract/:contractId/previous-delivery/:reportId/report',
        element: (
          <SupplierLayouts.SetupLayout>
            <BackfillReportPage />
          </SupplierLayouts.SetupLayout>
        ),
      },
      {
        path: 'contract/:contractId/success',
        element: (
          <AuthLayout>
            <ContractSuccessPage />
          </AuthLayout>
        ),
      },
      {
        path: 'preferences/success',
        element: (
          <AuthLayout>
            <Outlet />
            <SuccessPage />,
          </AuthLayout>
        ),
      },
      {
        path: 'applications',
        element: (
          <Suspense fallback={<SplashScreen />}>
            <ProtectedLayout allowedRoles={['PROCURER']} />
          </Suspense>
        ),
        children: [
          {
            path: ':applicationId',
            element: (
              <PublicContractsLayout>
                <SingleApplicationPage />
              </PublicContractsLayout>
            ),
          },
        ],
      },
      {
        path: 'contracts',
        element: (
          <Suspense fallback={<SplashScreen />}>
            <ProtectedLayout allowedRoles={['PROCURER']} />
          </Suspense>
        ),
        children: [
          {
            path: ':contractId/review-report/:reportId',
            // path: 'review-report/:reportId',
            element: (
              <PublicContractsLayout>
                <ReviewReportPage />
              </PublicContractsLayout>
            ),
          },
        ],
      },
    ],
  },
];
